import { useState } from "react";
import { Check, AlertTriangle } from "lucide-react";

const SERVICES = [
  { key: "starters", course: "starter", label: "Entrées", items: "starters" },
  { key: "mains", course: "main", label: "Plats", items: "mains" },
  { key: "desserts", course: "dessert", label: "Desserts", items: "desserts" },
];

export default function MenusTab({ wedding, catalog, patch }) {
  const sel = wedding.menu_selection || {};
  const sc = wedding.service_config || {};
  const adultServices = sc.adult_services || ["starter", "main", "dessert"];
  const childServices = sc.child_services || ["main", "dessert"];
  const maxProps = sc.max_proposals_per_service || 2;
  const [notice, setNotice] = useState("");
  const locked = wedding.locked;

  const assignedCount = (id) =>
    (wedding.guests || []).filter((g) => g.starter_id === id || g.main_id === id || g.dessert_id === id).length;

  const toggle = async (key, id) => {
    const current = sel[key] || [];
    const isOn = current.includes(id);
    if (isOn) {
      const n = assignedCount(id);
      if (n > 0) {
        setNotice(`Cette proposition est déjà choisie par ${n} invité(s). Réaffectez-les d'abord dans « Nos invités ».`);
        return;
      }
    } else if (current.length >= maxProps) {
      setNotice(`${maxProps} propositions maximum par service. Retirez-en une avant d'en ajouter une autre.`);
      return;
    }
    setNotice("");
    const next = isOn ? current.filter((x) => x !== id) : [...current, id];
    await patch({ menu_selection: { ...sel, [key]: next } });
  };

  const Block = ({ title, subtitle, items, selKey }) => (
    <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-2xl font-light">{title}</h2>
        <span className="overline">{(sel[selKey] || []).length}/{maxProps}</span>
      </div>
      {subtitle && <p className="mt-2 text-sm text-[#697585]">{subtitle}</p>}
      <ul className="mt-5 space-y-2">
        {(items || []).map((it) => {
          const on = (sel[selKey] || []).includes(it.id);
          return (
            <li key={it.id}>
              <button
                data-testid={`menu-option-${selKey}-${it.id}`}
                disabled={locked}
                onClick={() => toggle(selKey, it.id)}
                className={`w-full text-left px-4 py-3 border transition-colors flex items-start gap-3 ${
                  on ? "border-[#B8956A] bg-[#B8956A]/8" : "border-[#D9D3C5] hover:border-[#B8956A]/60"
                }`}
              >
                <span className={`mt-0.5 h-4 w-4 shrink-0 border flex items-center justify-center ${on ? "bg-[#B8956A] border-[#B8956A]" : "border-[#C9C0AB]"}`}>
                  {on && <Check size={11} className="text-white" />}
                </span>
                <span className="text-sm">{it.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );

  return (
    <div data-testid="menus-tab" className="space-y-8">
      <p className="text-sm text-[#697585] max-w-2xl">
        Retenez jusqu'à <strong>{maxProps} propositions par service</strong>. Chaque invité choisira ensuite parmi
        celles-ci dans l'onglet « Nos invités ».
      </p>

      {notice && (
        <p data-testid="menus-notice" className="flex gap-2 text-sm text-[#B8956A] border-l-2 border-[#B8956A] pl-3">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {notice}
        </p>
      )}

      {SERVICES.filter((s) => adultServices.includes(s.course)).map((s) => (
        <Block
          key={s.key}
          title={s.label}
          subtitle={s.course === "starter" && !childServices.includes("starter") ? "Les enfants n'ont pas d'entrée." : ""}
          items={catalog[s.items]}
          selKey={s.key}
        />
      ))}

      {!!childServices.length && (
        <>
          <div className="pt-2">
            <p className="overline">Menu enfant</p>
            <p className="mt-2 text-sm text-[#697585] max-w-2xl">
              Parmi la sélection autorisée par le restaurant. Si vous ne choisissez rien ici,
              les enfants piochent dans vos propositions adultes.
            </p>
          </div>
          {childServices.includes("main") && <Block title="Plats enfants" items={catalog.child_mains} selKey="child_mains" />}
          {childServices.includes("dessert") && <Block title="Desserts enfants" items={catalog.child_desserts} selKey="child_desserts" />}
        </>
      )}
    </div>
  );
}
